/**
 * PromptWindowContent - Renders the real PromptDetailPage inside a tiled window.
 *
 * Wraps PromptDetailPage in CapabilityDetailProvider (which the route-level
 * CapabilityDetailPage normally provides) and passes appKey/name as props
 * so the page works without route params.
 *
 * Pushes its own header layer so the global header reflects this window's
 * capability actions when the window is focused/topmost.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCapabilityDetail as useCapabilityDetailData } from '@/hooks/use-entity-detail';
import { CapabilityDetailProvider } from '@/lib/capability-detail-context';
import { useHeaderAction } from '@/lib/header-action-context';
import { useCapabilityActions } from '@/hooks/use-capability-actions';
import { CapabilityHeaderActions } from '@/components/capability-detail/CapabilityHeaderActions';
import { api } from '@/lib/api-client';
import { toast } from 'sonner';
import { Archive, Pencil } from 'lucide-react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import PromptDetailPage from '@/pages/prompts/prompt-detail-page';
import { useTiledWindows } from '@/components/tiled-windows';
import type { PromptWindow } from '@/types/tiled-window';

interface PromptWindowContentProps {
    window: PromptWindow;
}

function PromptWindowInner({ window: win }: PromptWindowContentProps) {
    const appKey = win.appKey ?? '';
    const name = win.entityName;
    const headerLayerId = `window-${win.id}`;
    const { data, refetch } = useCapabilityDetailData(appKey, name);
    const { pushHeaderLayer, removeHeaderLayer } = useHeaderAction();
    const { closeWindow } = useTiledWindows();
    const queryClient = useQueryClient();

    const actions = useCapabilityActions({
        appKey,
        name,
        typeLabel: 'Prompt',
        data: data as Record<string, unknown> | null,
        refetch,
    });

    const saveRef = useRef(actions.save);
    saveRef.current = actions.save;
    const publishRef = useRef(actions.publish);
    publishRef.current = actions.publish;
    const editRef = useRef(actions.edit);
    editRef.current = actions.edit;

    useEffect(() => {
        if (!data || actions.isArchived) {
            removeHeaderLayer(headerLayerId);
            return;
        }
        pushHeaderLayer(headerLayerId, {
            action: (
                <CapabilityHeaderActions
                    typeLabel="Prompt"
                    hasWorkingRevision={actions.hasWorkingRevision}
                    isViewingPublished={actions.isViewingPublished}
                    isArchived={actions.isArchived}
                    selectedPublishedLabel={actions.selectedPublishedLabel}
                    isDirty={actions.isDirty}
                    isSaving={actions.isSaving}
                    isPublishing={actions.isPublishing}
                    onSave={() => void saveRef.current()}
                    onPublish={() => void publishRef.current()}
                    onEdit={() => editRef.current()}
                    onArchive={() => actions.openArchiveDialog()}
                    onSettings={() => actions.openSettings()}
                />
            ),
        });
        return () => removeHeaderLayer(headerLayerId);
    }, [
        data, headerLayerId,
        actions.hasWorkingRevision, actions.isViewingPublished, actions.isArchived,
        actions.selectedPublishedLabel, actions.isDirty, actions.isSaving, actions.isPublishing,
        pushHeaderLayer, removeHeaderLayer,
    ]);

    return (
        <>
            <div className="h-full w-full overflow-auto">
                <PromptDetailPage appKey={appKey} name={name} headerLayerId={headerLayerId} />
            </div>
            <AlertDialog open={actions.replaceDialogOpen} onOpenChange={actions.setReplaceDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <Pencil size={18} className="text-amber-600" />
                            Replace working copy?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            A working copy already exists. Replacing it will discard any unsaved changes
                            and create a new working copy based on the selected published version.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={actions.handleConfirmReplace}>
                            Replace
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}

export function PromptWindowContent({ window: win }: PromptWindowContentProps) {
    const appKey = win.appKey ?? '';
    const name = win.entityName;
    const queryClient = useQueryClient();
    const { closeWindow } = useTiledWindows();

    const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
    const [isArchiving, setIsArchiving] = useState(false);
    const openArchiveDialog = useCallback(() => setArchiveDialogOpen(true), []);

    const handleArchive = useCallback(async () => {
        if (!appKey || !name) return;
        setIsArchiving(true);
        try {
            await api.delete(`/applications/${appKey}/capabilities/${encodeURIComponent(name)}`);
            toast.success('Prompt archived successfully');
            queryClient.invalidateQueries({ queryKey: ['capabilities'] });
            closeWindow(win.id);
        } catch {
            toast.error('Failed to archive prompt');
        } finally {
            setIsArchiving(false);
            setArchiveDialogOpen(false);
        }
    }, [appKey, name, queryClient, closeWindow, win.id]);

    return (
        <CapabilityDetailProvider openArchiveDialog={openArchiveDialog}>
            <PromptWindowInner window={win} />

            <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <Archive size={18} className="text-destructive" />
                            Archive Prompt
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to archive &quot;{name}&quot;? It will no longer
                            appear in the active prompt list.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isArchiving}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault();
                                void handleArchive();
                            }}
                            disabled={isArchiving}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {isArchiving ? 'Archiving...' : 'Archive'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </CapabilityDetailProvider>
    );
}
